import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, RefreshCw, Database, Cpu, ChevronRight, AlertTriangle, Archive, ArchiveRestore, Power, PowerOff, PackagePlus } from 'lucide-react';
import { puntovService } from '../../../services/api';
import { useAuth } from '../../../hooks/useAuth';
import FormularioCliente from './FormularioCliente';
import FormularioMaquina from './FormularioMaquina';
import AccesosCliente from './AccesosCliente';
import DialogoArchivar from './DialogoArchivar';
import DialogoClasificacion from './DialogoClasificacion';
import TablaDatos from './TablaDatos';
import DialogoSuspension from './DialogoSuspension';
import DialogoEstadoMaquina from './DialogoEstadoMaquina';
import DialogoAlmacenPrueba from './DialogoAlmacenPrueba';

// Clasificación de la base para los reportes del ERP (no cambia nada en Punto V).
const ETIQUETA_CLASIFICACION = {
  zona_propia: { texto: 'Zona Punto V', clase: 'bg-blue-50 text-blue-700' },
  cliente: { texto: 'Cliente', clase: 'bg-gray-100 text-gray-700' }
};

const Cargando = () => (
  <div className="text-center py-12">
    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
  </div>
);

const Aviso = ({ children, tipo = 'error' }) => (
  <div className={`rounded-xl p-4 text-sm border flex items-start gap-2 ${
    tipo === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
  }`}>
    {tipo === 'error' && <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />}
    <span>{children}</span>
  </div>
);

const textoBusquedaCliente = (c) =>
  `${c.slug} ${c.base_datos} ${c.duenio.nombre || ''} ${c.duenio.correo || ''} ${c.motivo_archivo || ''}`;

const claveCliente = (c) => c.base_datos;

const ClientesMaquinas = () => {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'admin';

  const [clientes, setClientes] = useState([]);
  const [basesVerificadas, setBasesVerificadas] = useState(true);
  const [filtroBase, setFiltroBase] = useState('servidor');
  const [porSuspender, setPorSuspender] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');
  const [mostrarFormCliente, setMostrarFormCliente] = useState(false);
  const [verArchivados, setVerArchivados] = useState(false);
  const [archivando, setArchivando] = useState(null);
  const [porArchivar, setPorArchivar] = useState(null);
  const [porClasificar, setPorClasificar] = useState(null);

  const [seleccionado, setSeleccionado] = useState(null);
  const [maquinas, setMaquinas] = useState([]);
  const [loadingMaquinas, setLoadingMaquinas] = useState(false);
  const [errorMaquinas, setErrorMaquinas] = useState('');
  const [mostrarFormMaquina, setMostrarFormMaquina] = useState(false);
  const [maquinaEstado, setMaquinaEstado] = useState(null);
  const [porCargarAlmacen, setPorCargarAlmacen] = useState(null);

  const cargarClientes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await puntovService.listarClientes({ incluirSinBase: true });
      setClientes(res.data || []);
      setBasesVerificadas(res.bases_verificadas !== false);
    } catch (err) {
      setError(err.message || 'Error al cargar los clientes de Punto V');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarClientes(); }, [cargarClientes]);

  const peticionMaquinas = useRef(0);
  const cargarMaquinas = useCallback(async (cliente) => {
    const mia = ++peticionMaquinas.current;
    setLoadingMaquinas(true);
    setErrorMaquinas('');
    setMaquinas([]);
    try {
      const res = await puntovService.listarMaquinas(cliente.duenio.id);
      if (mia === peticionMaquinas.current) setMaquinas(res.data?.maquinas || []);
    } catch (err) {
      if (mia === peticionMaquinas.current) setErrorMaquinas(err.message || 'Error al cargar las máquinas');
    } finally {
      if (mia === peticionMaquinas.current) setLoadingMaquinas(false);
    }
  }, []);

  const seleccionar = (cliente) => {
    setSeleccionado(cliente);
    setMaquinaEstado(null);
    setMostrarFormMaquina(false);
    setExito('');
    cargarMaquinas(cliente);
  };

  const totalArchivados = useMemo(() => clientes.filter(c => c.archivado).length, [clientes]);

  // base_existe null = Punto V no pudo verificarlo: se trata como en el servidor.
  const enServidor = (c) => c.base_existe !== false;
  const conteoBase = useMemo(() => {
    const visibles = clientes.filter(c => !!c.archivado === verArchivados);
    const servidor = visibles.filter(enServidor).length;
    return { servidor, sinBase: visibles.length - servidor, todas: visibles.length };
  }, [clientes, verArchivados]);

  const filtrados = useMemo(
    () => clientes.filter(c => !!c.archivado === verArchivados
      && (filtroBase === 'todas' || (filtroBase === 'servidor') === enServidor(c))),
    [clientes, verArchivados, filtroBase]
  );

  const pedirSuspension = useCallback((cliente, e) => {
    e.stopPropagation();
    setPorSuspender(cliente);
  }, []);

  // Si falla, el error se propaga al diálogo y se muestra ahí.
  const confirmarSuspension = async (motivo) => {
    const cliente = porSuspender;
    if (cliente.suspendida) {
      await puntovService.reactivarCliente(cliente.base_datos);
      setExito(`Cliente ${cliente.slug} reactivado: sus usuarios y máquinas vuelven a funcionar.`);
    } else {
      await puntovService.suspenderCliente(cliente.base_datos, motivo);
      setExito(`Cliente ${cliente.slug} suspendido: no puede entrar al panel y sus máquinas no autorizan ventas.`);
    }
    setPorSuspender(null);
    await cargarClientes();
  };

  const cancelarSuspension = useCallback(() => setPorSuspender(null), []);

  // Si falla, el error se propaga al diálogo y se muestra ahí.
  const confirmarEstadoMaquina = async () => {
    const maquina = maquinaEstado;
    const activa = String(maquina.status) !== '1';
    const res = await puntovService.cambiarEstadoMaquina(seleccionado.base_datos, maquina.id_maquina, activa);
    setMaquinaEstado(null);
    setExito(res.message);
    await cargarMaquinas(seleccionado);
  };

  const cancelarEstadoMaquina = useCallback(() => setMaquinaEstado(null), []);

  // Si falla, el error se propaga al diálogo y se muestra ahí.
  const confirmarAlmacenPrueba = async ({ conStock }) => {
    const res = await puntovService.cargarAlmacenPrueba(porCargarAlmacen.base_datos, { conStock });
    setPorCargarAlmacen(null);
    setExito(res.message);
  };

  const cancelarAlmacenPrueba = useCallback(() => setPorCargarAlmacen(null), []);

  // Archivar abre el diálogo (pide motivo); restaurar es inmediato porque
  // no oculta nada.
  const alternarArchivo = useCallback(async (cliente, e) => {
    e.stopPropagation();
    if (!cliente.archivado) {
      setPorArchivar(cliente);
      return;
    }
    setArchivando(cliente.base_datos);
    setError('');
    try {
      await puntovService.restaurarCliente(cliente.base_datos);
      setExito(`Cliente ${cliente.slug} restaurado`);
      await cargarClientes();
    } catch (err) {
      setError(err.message || 'Error al restaurar el cliente');
    } finally {
      setArchivando(null);
    }
  }, [cargarClientes]);

  // Si falla, el error se propaga al diálogo y se muestra ahí.
  const confirmarArchivo = async (motivo) => {
    const cliente = porArchivar;
    await puntovService.archivarCliente(cliente.base_datos, motivo);
    setPorArchivar(null);
    setExito(`Cliente ${cliente.slug} archivado`);
    if (seleccionado?.base_datos === cliente.base_datos) setSeleccionado(null);
    await cargarClientes();
  };

  const cancelarArchivo = useCallback(() => setPorArchivar(null), []);

  const pedirClasificacion = useCallback((cliente, e) => {
    e.stopPropagation();
    setPorClasificar(cliente);
  }, []);

  // Si falla, el error se propaga al diálogo y se muestra ahí.
  const confirmarClasificacion = async (datos) => {
    const cliente = porClasificar;
    await puntovService.clasificarCliente(cliente.base_datos, datos);
    setPorClasificar(null);
    setExito(datos.tipo
      ? `${cliente.slug} quedó como ${ETIQUETA_CLASIFICACION[datos.tipo].texto}${datos.zona ? ` (${datos.zona})` : ''}`
      : `${cliente.slug} quedó sin clasificar`);
    await cargarClientes();
  };

  const cancelarClasificacion = useCallback(() => setPorClasificar(null), []);

  const columnas = useMemo(() => [
    {
      id: 'cliente',
      titulo: 'Cliente',
      ordenable: true,
      valor: (c) => c.slug,
      render: (c) => (
        <>
          <div className="font-medium text-gray-800">{c.slug}</div>
          <div className="text-xs text-gray-400">{c.base_datos}</div>
          {c.base_existe === false && c.base_parecida && (
            <div className="text-xs text-amber-700 mt-0.5" title="En el servidor los nombres de base distinguen mayúsculas: son bases distintas">
              En el servidor existe como <span className="font-mono">{c.base_parecida}</span>
            </div>
          )}
          {c.es_cuenta_erp && (
            <span
              title="Empresa de la cuenta con la que el ERP se conecta a Punto V: no se puede suspender"
              className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-medium"
            >
              Cuenta del ERP
            </span>
          )}
          {c.archivado && c.motivo_archivo && (
            <div className="text-xs text-amber-700 mt-0.5">Archivado: {c.motivo_archivo}</div>
          )}
        </>
      )
    },
    {
      id: 'duenio',
      titulo: 'Dueño',
      ordenable: true,
      valor: (c) => c.duenio.nombre,
      render: (c) => (
        <>
          <div className="text-gray-700">{c.duenio.nombre}</div>
          <div className="text-xs text-gray-400">{c.duenio.correo}</div>
        </>
      )
    },
    {
      id: 'tipo',
      titulo: 'Tipo',
      ordenable: true,
      valor: (c) => c.clasificacion?.tipo || 'sin_clasificar',
      filtro: [
        { valor: 'zona_propia', etiqueta: 'Zona Punto V' },
        { valor: 'cliente', etiqueta: 'Cliente' },
        { valor: 'sin_clasificar', etiqueta: 'Sin clasificar' }
      ],
      render: (c) => {
        const e = ETIQUETA_CLASIFICACION[c.clasificacion?.tipo];
        const contenido = e ? (
          <>
            <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${e.clase}`}>{e.texto}</span>
            {c.clasificacion.zona && <div className="text-xs text-gray-500 mt-0.5">{c.clasificacion.zona}</div>}
          </>
        ) : <span className="text-xs text-gray-400">Sin clasificar</span>;
        if (!esAdmin || c.es_cuenta_erp) return contenido;
        return (
          <button
            onClick={(ev) => pedirClasificacion(c, ev)}
            title="Clasificar como zona Punto V o cliente (solo para reportes del ERP)"
            className="text-left rounded hover:bg-gray-100 px-1 -mx-1 py-0.5"
          >
            {contenido}
          </button>
        );
      }
    },
    {
      id: 'usuarios',
      titulo: 'Usuarios',
      ordenable: true,
      alinear: 'center',
      valor: (c) => c.total_usuarios
    },
    {
      id: 'estado',
      titulo: 'Estado',
      ordenable: true,
      valor: (c) => (c.suspendida ? 'suspendido' : 'activo'),
      filtro: [
        { valor: 'activo', etiqueta: 'Activo' },
        { valor: 'suspendido', etiqueta: 'Suspendido' }
      ],
      render: (c) => (c.suspendida
        ? (
          <span
            title={c.suspension?.motivo ? `Motivo: ${c.suspension.motivo}` : undefined}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-red-200 bg-red-50 text-xs font-medium text-red-700"
          >
            <PowerOff size={11} /> Suspendido
          </span>
        )
        : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-green-200 bg-green-50 text-xs font-medium text-green-700">
            <Power size={11} /> Activo
          </span>
        ))
    },
    {
      id: 'acciones',
      titulo: '',
      alinear: 'right',
      valor: () => '',
      render: (c) => (
        <div className="flex items-center justify-end gap-2">
          {esAdmin && c.suspendida !== null && !c.es_cuenta_erp && (
            <button
              onClick={(e) => pedirSuspension(c, e)}
              title={c.suspendida ? 'Reactivar el servicio en Punto V' : 'Suspender el servicio en Punto V (panel y máquinas)'}
              aria-label={c.suspendida ? `Reactivar ${c.slug}` : `Suspender ${c.slug}`}
              className={`p-1 ${c.suspendida ? 'text-red-500 hover:text-green-600' : 'text-gray-400 hover:text-red-600'}`}
            >
              {c.suspendida ? <Power size={16} /> : <PowerOff size={16} />}
            </button>
          )}
          {esAdmin && (
            <button
              onClick={(e) => alternarArchivo(c, e)}
              disabled={archivando === c.base_datos}
              title={c.archivado ? 'Restaurar a la lista' : 'Archivar (solo en el ERP)'}
              aria-label={c.archivado ? `Restaurar ${c.slug}` : `Archivar ${c.slug}`}
              className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-40"
            >
              {c.archivado ? <ArchiveRestore size={16} /> : <Archive size={16} />}
            </button>
          )}
          <ChevronRight size={16} className="text-gray-300" />
        </div>
      )
    }
  ], [esAdmin, archivando, alternarArchivo, pedirSuspension, pedirClasificacion]);

  const cambiarVista = (archivados) => {
    setVerArchivados(archivados);
    setSeleccionado(null);
  };

  const alCrearCliente = async (resultado) => {
    setMostrarFormCliente(false);
    const almacen = resultado.almacen_prueba;
    setExito(almacen?.cargado
      ? `Cliente creado: base ${resultado.base_datos}, con ${almacen.productos ?? 'los'} productos de prueba ${almacen.con_stock === false ? 'sin stock' : 'con stock'} en el almacén`
      : `Cliente creado: base ${resultado.base_datos}`);
    // El cliente sí quedó creado; solo falló la carga del almacén.
    if (almacen && !almacen.cargado) {
      setError(`No se cargó el almacén de prueba: ${almacen.error}. Puede cargarlo desde el panel del cliente.`);
    }
    await cargarClientes();
  };

  const alCrearMaquina = async (resultado) => {
    setMostrarFormMaquina(false);
    setExito(resultado.sin_posiciones
      ? 'Máquina registrada. Su tipo no tiene posiciones predefinidas en Punto V: hay que cargarlas desde Punto V Manager.'
      : 'Máquina registrada en Punto V');
    await cargarMaquinas(seleccionado);
  };

  return (
    <div className="space-y-4">
      {exito && <Aviso tipo="exito">{exito}</Aviso>}

      {maquinaEstado && seleccionado && (
        <DialogoEstadoMaquina
          maquina={maquinaEstado}
          cliente={seleccionado}
          onConfirmar={confirmarEstadoMaquina}
          onCancel={cancelarEstadoMaquina}
        />
      )}

      {porCargarAlmacen && (
        <DialogoAlmacenPrueba cliente={porCargarAlmacen} onConfirmar={confirmarAlmacenPrueba} onCancel={cancelarAlmacenPrueba} />
      )}

      {porSuspender && (
        <DialogoSuspension cliente={porSuspender} onConfirmar={confirmarSuspension} onCancel={cancelarSuspension} />
      )}

      {porArchivar && (
        <DialogoArchivar cliente={porArchivar} onConfirmar={confirmarArchivo} onCancel={cancelarArchivo} />
      )}
      {porClasificar && (
        <DialogoClasificacion cliente={porClasificar} onConfirmar={confirmarClasificacion} onCancel={cancelarClasificacion} />
      )}

      <div className="flex justify-end">
        <div className="flex gap-2">
          <button
            onClick={cargarClientes}
            disabled={loading}
            className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualizar
          </button>
          {esAdmin && (
            <button
              onClick={() => { setMostrarFormCliente(true); setExito(''); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
            >
              <Plus size={16} /> Nuevo cliente
            </button>
          )}
        </div>
      </div>

      {mostrarFormCliente && (
        <FormularioCliente onCreado={alCrearCliente} onCancel={() => setMostrarFormCliente(false)} />
      )}

      {error && <Aviso>{error}</Aviso>}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-3 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <Database size={16} className="text-gray-400" />
            <h3 className="font-semibold text-gray-800 text-sm">Clientes</h3>
            <div className="ml-auto flex bg-gray-100 rounded-lg p-0.5 text-xs">
              <button
                onClick={() => cambiarVista(false)}
                className={`px-3 py-1 rounded-md ${!verArchivados ? 'bg-white shadow-sm text-gray-800 font-medium' : 'text-gray-500'}`}
              >
                Activos ({clientes.length - totalArchivados})
              </button>
              <button
                onClick={() => cambiarVista(true)}
                className={`px-3 py-1 rounded-md flex items-center gap-1 ${verArchivados ? 'bg-white shadow-sm text-gray-800 font-medium' : 'text-gray-500'}`}
              >
                <Archive size={12} /> Archivados ({totalArchivados})
              </button>
            </div>
            <select
              value={filtroBase}
              onChange={(e) => { setFiltroBase(e.target.value); setSeleccionado(null); }}
              aria-label="Filtrar por base de datos en el servidor"
              className={`px-2 py-1 border rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 ${
                filtroBase === 'servidor' ? 'border-gray-200 text-gray-600' : 'border-amber-300 bg-amber-50 text-amber-800'
              }`}
            >
              <option value="servidor">En el servidor ({conteoBase.servidor})</option>
              <option value="sin_base">Sin base en el servidor ({conteoBase.sinBase})</option>
              <option value="todas">Todas ({conteoBase.todas})</option>
            </select>
          </div>
          {/* Solo en la primera carga: al recargar se conserva la tabla con su página y filtros. */}
          {loading && clientes.length === 0 ? <Cargando /> : (
            <TablaDatos
              columnas={columnas}
              filas={filtrados}
              clave={claveCliente}
              textoBusqueda={textoBusquedaCliente}
              onFilaClick={seleccionar}
              filaActiva={(c) => seleccionado?.base_datos === c.base_datos}
              placeholderBusqueda="Buscar cliente, dueño o correo"
              vacio={error ? '—' : verArchivados ? 'No hay clientes archivados' : 'Sin clientes'}
            />
          )}
          {!(loading && clientes.length === 0) && !basesVerificadas && (
            <p className="px-4 py-2.5 text-xs border-t border-gray-100 text-amber-700 bg-amber-50">
              No se pudo verificar qué bases existen en el servidor de Punto V: se muestran todos los clientes.
            </p>
          )}
          {!(loading && clientes.length === 0) && basesVerificadas && filtroBase === 'servidor' && conteoBase.sinBase > 0 && (
            <p className="px-4 py-2.5 text-xs border-t border-gray-100 text-gray-400">
              {conteoBase.sinBase} cliente{conteoBase.sinBase === 1 ? '' : 's'} sin base de datos en el servidor no se muestra{conteoBase.sinBase === 1 ? '' : 'n'}.{' '}
              <button type="button" onClick={() => setFiltroBase('sin_base')} className="text-blue-600 hover:underline">Ver</button>
            </p>
          )}
          {filtroBase === 'sin_base' && conteoBase.sinBase > 0 && (
            <p className="px-4 py-2.5 text-xs border-t border-gray-100 text-amber-700 bg-amber-50">
              Estos clientes están registrados en Punto V pero su base de datos no existe en el servidor: no se pueden ver ni crear sus máquinas.
            </p>
          )}
        </div>

        <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <Cpu size={16} className="text-gray-400" />
            <h3 className="font-semibold text-gray-800 text-sm truncate">
              {seleccionado ? `Máquinas de ${seleccionado.slug}` : 'Máquinas'}
            </h3>
            {seleccionado && esAdmin && enServidor(seleccionado) && (
              <button
                onClick={() => { setPorCargarAlmacen(seleccionado); setExito(''); }}
                title="Cargar el catálogo de prueba en el almacén vacío del cliente"
                className="ml-auto px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-1 text-xs font-medium"
              >
                <PackagePlus size={14} /> Almacén de prueba
              </button>
            )}
            {seleccionado && esAdmin && !mostrarFormMaquina && !loadingMaquinas && !errorMaquinas && (
              <button
                onClick={() => { setMostrarFormMaquina(true); setExito(''); }}
                className={`${enServidor(seleccionado) ? '' : 'ml-auto '}px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1 text-xs font-medium`}
              >
                <Plus size={14} /> Nueva máquina
              </button>
            )}
          </div>

          {!seleccionado ? (
            <p className="px-4 py-10 text-center text-sm text-gray-400">Seleccione un cliente para ver sus máquinas</p>
          ) : (
            <div className="p-4 space-y-4">
              {mostrarFormMaquina && !errorMaquinas && (
                <FormularioMaquina
                  cliente={seleccionado}
                  onCreado={alCrearMaquina}
                  onCancel={() => setMostrarFormMaquina(false)}
                />
              )}
              {loadingMaquinas ? <Cargando /> : errorMaquinas ? <Aviso>{errorMaquinas}</Aviso> : (
                maquinas.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-400">Este cliente no tiene máquinas registradas</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                        <th className="py-2">Id</th>
                        <th className="py-2">Descripción</th>
                        <th className="py-2">Estado</th>
                        {esAdmin && <th className="py-2" />}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Punto V permite ids repetidos en db_maquinas: la key no puede ser solo el id. */}
                      {maquinas.map((m, i) => (
                        <tr key={`${m.id_maquina}-${i}`} className="border-b border-gray-100">
                          <td className="py-2 font-mono text-xs text-gray-800">{m.id_maquina}</td>
                          <td className="py-2 text-gray-600">{m.descripcion_maq || '—'}</td>
                          <td className="py-2">
                            {String(m.status) === '1'
                              ? <span className="text-xs font-medium text-green-700">Activa</span>
                              : <span className="text-xs font-medium text-gray-400">Inactiva</span>}
                          </td>
                          {esAdmin && (
                            <td className="py-2 text-right">
                              <button
                                type="button"
                                onClick={() => setMaquinaEstado(m)}
                                title={String(m.status) === '1' ? 'Inactivar la máquina en Punto V' : 'Activar la máquina en Punto V'}
                                aria-label={String(m.status) === '1' ? `Inactivar ${m.id_maquina}` : `Activar ${m.id_maquina}`}
                                className={`p-1 ${String(m.status) === '1' ? 'text-gray-400 hover:text-red-600' : 'text-red-500 hover:text-green-600'}`}
                              >
                                {String(m.status) === '1' ? <PowerOff size={15} /> : <Power size={15} />}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {seleccionado && enServidor(seleccionado) && (
        <AccesosCliente cliente={seleccionado} maquinas={maquinas} esAdmin={esAdmin} />
      )}
    </div>
  );
};

export default ClientesMaquinas;
